import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, TriangleAlert as AlertTriangle, ArrowUpRight, Check, ChevronDown, Settings } from 'lucide-react';
import { formatDuration, formatDurationFromMs } from '../utils';
import type { Site, UptimeEvent } from '../types';

interface UptimeMonitorWidgetProps {
  site: Site;
  uptimeEvents: UptimeEvent[];
  onToggleUptime: () => void;
  onUpdateInterval: (minutes: number) => void;
  savingInterval: boolean;
}

export const UptimeMonitorWidget: React.FC<UptimeMonitorWidgetProps> = ({
  site,
  uptimeEvents,
  onToggleUptime,
  onUpdateInterval,
  savingInterval,
}) => {
  const [showUptimeConfig, setShowUptimeConfig] = useState(false);
  const [isEventsExpanded, setIsEventsExpanded] = useState(true);

  return (
    <div className="bg-card border border-border rounded-xl px-5 pt-5 pb-4 shadow-sm w-full h-full flex flex-col">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-extrabold text-foreground tracking-tight">Uptime Monitor</h3>
        <div className="relative">
          <button
            onClick={() => setShowUptimeConfig(!showUptimeConfig)}
            className="p-1.5 hover:bg-muted rounded-lg transition-colors text-muted-foreground group"
            title="Frequency Settings"
          >
            <Settings className={`w-4 h-4 group-hover:rotate-45 transition-transform ${showUptimeConfig ? 'text-workspace-accent' : ''}`} />
          </button>

          {showUptimeConfig && (
            <div className="absolute right-0 mt-2 w-44 bg-popover border border-border rounded-xl shadow-xl z-50 p-1.5 flex flex-col gap-0.5">
              <p className="text-[11px] font-bold text-muted-foreground px-3 py-1 uppercase tracking-wider">Frequency</p>
              {[5, 10].map(min => (
                <button
                  key={min}
                  onClick={() => onUpdateInterval(min)}
                  disabled={savingInterval}
                  className={`flex items-center justify-between px-3 py-2 text-xs font-bold rounded-lg transition-colors ${site.uptime_interval === min ? 'bg-workspace-accent/10 text-workspace-accent' : 'hover:bg-muted text-muted-foreground'}`}
                >
                  Every {min} Minutes
                  {site.uptime_interval === min && <Check className="w-3 h-3" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-colors ${!site.uptime_enabled ? 'border-muted text-muted-foreground' : (site.uptime_status === 'down' ? 'border-red-500/80 text-red-500' : 'border-emerald-500/80 text-emerald-500')}`}>
            {!site.uptime_enabled ? (
              <Activity className="w-5 h-5 opacity-50" />
            ) : site.uptime_status === 'down' ? (
              <AlertTriangle className="w-5 h-5" />
            ) : (
              <ArrowUpRight className="w-5 h-5" />
            )}
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-foreground leading-none">
              {!site.uptime_enabled
                ? 'Monitoring Paused'
                : (site.uptime_status === 'down'
                  ? (site.uptime_last_down_at ? `Down for ${formatDuration(site.uptime_last_down_at)}` : 'Site Offline')
                  : (site.uptime_last_up_at ? `Running for ${formatDuration(site.uptime_last_up_at)}` : 'Site Online')
                )
              }
            </span>
            {site.uptime_enabled && site.uptime_last_check && (
              <span className="text-[11px] font-medium text-muted-foreground mt-1.5 opacity-70">
                Last check: {formatDuration(site.uptime_last_check)} ago
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <span className={`text-[11px] font-black ${!site.uptime_enabled ? 'text-muted-foreground' : 'text-workspace-accent'}`}>
            {site.uptime_enabled ? 'ON' : 'OFF'}
          </span>
          <button
            onClick={onToggleUptime}
            className={`w-9 h-5 rounded-full relative shadow-inner transition-all flex items-center px-1 ${!site.uptime_enabled ? 'bg-muted' : 'bg-workspace-accent'}`}
          >
            <motion.div
              initial={false}
              animate={{ x: site.uptime_enabled ? 16 : 0 }}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
              className="w-3 h-3 bg-white rounded-full shadow-sm"
            />
          </button>
        </div>
      </div>

      {site.uptime_enabled && (
        <div className="mt-5 pt-4 border-t border-border/50 flex-1">
          <div className="flex items-center justify-between mb-3 group cursor-pointer" onClick={() => setIsEventsExpanded(!isEventsExpanded)}>
            <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Latest events</h4>
            <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${isEventsExpanded ? 'rotate-180' : ''}`} />
          </div>

          <AnimatePresence>
            {isEventsExpanded && (
              <motion.div
                key="uptime-events"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden flex flex-col gap-3 h-full"
              >
                <div className="flex-1 flex flex-col gap-3">
                  {uptimeEvents.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No events recorded yet</p>
                  ) : (
                    uptimeEvents.map((event, idx) => {
                      const eventDate = new Date(event.created_at);
                      const dateStr = eventDate.toLocaleString(undefined, {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: true
                      });

                      const nextTime = idx === 0 ? Date.now() : new Date(uptimeEvents[idx - 1].created_at).getTime();
                      const durMs = Math.max(0, nextTime - eventDate.getTime());
                      const durStr = formatDurationFromMs(durMs);

                      return (
                        <div key={event.id} className="flex items-center justify-between group/evt">
                          <div className="flex items-center gap-3">
                            <div className={event.status === 'up' ? 'text-emerald-500' : 'text-red-500'}>
                              {event.status === 'up' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 7-7 7 7" /><path d="M12 19V5" /></svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></svg>
                              )}
                            </div>
                            <div className="text-xs font-medium text-foreground/80 dark:text-foreground/70">
                              {dateStr} <span className="text-muted-foreground/60 mx-1">for</span> {durStr}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <button
                  className="text-[11px] font-bold text-workspace-accent hover:underline self-end mt-1"
                  onClick={() => {}}
                >
                  view all
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
